import { useMutation } from "convex/react";
/**
 * useWriteConfirmation - Manages AI pending write actions
 *
 * This hook handles the confirmation flow for AI write operations:
 * 1. Listens for pending actions from AI tool responses
 * 2. Provides confirm/reject mutations
 * 3. Tracks action status
 */
import { useCallback, useEffect, useState } from "react";

import { aiPendingActionsConfirm, aiPendingActionsReject } from "~/api-refs";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";

// Regex patterns (top-level for performance)
const CONFIRMATION_REQUIRED_REGEX =
  /{[\S\s]*"type"\s*:\s*"confirmation_required"[\S\s]*}/;

/** Change preview structure */
export interface ChangePreview {
  field: string;
  from: string | number | boolean | null;
  to: string | number | boolean | null;
}

/** Pending action from AI response */
export interface PendingAction {
  pendingActionId: string;
  description: string;
  affectedEntity: {
    type: string;
    id: string;
    name: string;
  };
  changes: ChangePreview[];
  expiresAt: number;
  toolName: string;
}

/** Hook state */
interface UseWriteConfirmationReturn {
  /** Current pending action being shown */
  pendingAction: PendingAction | null;
  /** Show the confirmation dialog */
  showConfirmation: (action: PendingAction) => void;
  /** Hide the confirmation dialog */
  hideConfirmation: () => void;
  /** Confirm the pending action */
  confirm: () => Promise<void>;
  /** Reject the pending action */
  reject: () => Promise<void>;
  /** Whether an action is being processed */
  isProcessing: boolean;
  /** Whether the dialog is open */
  isOpen: boolean;
}

export function useWriteConfirmation(): UseWriteConfirmationReturn {
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const confirmMutation = useMutation(aiPendingActionsConfirm);
  const rejectMutation = useMutation(aiPendingActionsReject);

  const showConfirmation = useCallback((action: PendingAction) => {
    setPendingAction(action);
    setIsOpen(true);
  }, []);

  const hideConfirmation = useCallback(() => {
    setIsOpen(false);
    // Clear pending action after animation
    setTimeout(() => setPendingAction(null), 200);
  }, []);

  const confirm = useCallback(async () => {
    if (!pendingAction) {
      return;
    }
    setIsProcessing(true);
    try {
      await confirmMutation({
        pendingActionId: toConvexTableId(
          "aiPendingActions",
          pendingAction.pendingActionId
        ),
      });
      hideConfirmation();
    } catch (error) {
      logger.exception("ai_action_confirm_failed", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [pendingAction, confirmMutation, hideConfirmation]);

  const reject = useCallback(async () => {
    if (!pendingAction) {
      return;
    }
    setIsProcessing(true);
    try {
      await rejectMutation({
        pendingActionId: toConvexTableId(
          "aiPendingActions",
          pendingAction.pendingActionId
        ),
      });
      hideConfirmation();
    } catch (error) {
      logger.exception("ai_action_reject_failed", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [pendingAction, rejectMutation, hideConfirmation]);

  // Auto-close if action expires
  useEffect(() => {
    if (!pendingAction) {
      return undefined;
    }

    const timeUntilExpiry = pendingAction.expiresAt - Date.now();
    if (timeUntilExpiry <= 0) {
      hideConfirmation();
      return undefined;
    }

    const timeout = setTimeout(hideConfirmation, timeUntilExpiry);
    return () => clearTimeout(timeout);
  }, [pendingAction, hideConfirmation]);

  return {
    pendingAction,
    showConfirmation,
    hideConfirmation,
    confirm,
    reject,
    isProcessing,
    isOpen,
  };
}

function isChangePreview(value: unknown): value is ChangePreview {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if (!("field" in value) || typeof value.field !== "string") {
    return false;
  }
  return true;
}

function isAffectedEntity(
  value: unknown
): value is PendingAction["affectedEntity"] {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return (
    "type" in value &&
    typeof value.type === "string" &&
    "id" in value &&
    typeof value.id === "string" &&
    "name" in value &&
    typeof value.name === "string"
  );
}

/**
 * Parse AI tool response to extract pending action
 * Returns null if the message doesn't contain a confirmation request
 */
export function parsePendingAction(messageText: string): PendingAction | null {
  // Look for confirmation_required JSON in the message
  // The AI should include this in its response when a write tool returns confirmation_required
  try {
    // Try to find JSON-like structure in the message
    const jsonMatch = CONFIRMATION_REQUIRED_REGEX.exec(messageText);
    if (!jsonMatch) {
      return null;
    }

    const parsedUnknown: unknown = JSON.parse(jsonMatch[0]);
    if (typeof parsedUnknown !== "object" || parsedUnknown === null) {
      return null;
    }

    const type =
      "type" in parsedUnknown && typeof parsedUnknown.type === "string"
        ? parsedUnknown.type
        : undefined;
    const pendingActionId =
      "pendingActionId" in parsedUnknown &&
      typeof parsedUnknown.pendingActionId === "string"
        ? parsedUnknown.pendingActionId
        : undefined;
    const description =
      "description" in parsedUnknown &&
      typeof parsedUnknown.description === "string"
        ? parsedUnknown.description
        : undefined;
    const affectedEntity =
      "affectedEntity" in parsedUnknown &&
      isAffectedEntity(parsedUnknown.affectedEntity)
        ? parsedUnknown.affectedEntity
        : undefined;
    const changes =
      "changes" in parsedUnknown &&
      Array.isArray(parsedUnknown.changes) &&
      parsedUnknown.changes.every(isChangePreview)
        ? parsedUnknown.changes
        : undefined;
    const expiresAt =
      "expiresAt" in parsedUnknown &&
      typeof parsedUnknown.expiresAt === "number"
        ? parsedUnknown.expiresAt
        : undefined;
    const toolName =
      "toolName" in parsedUnknown && typeof parsedUnknown.toolName === "string"
        ? parsedUnknown.toolName
        : undefined;

    if (
      type === "confirmation_required" &&
      pendingActionId &&
      description &&
      affectedEntity &&
      changes
    ) {
      return {
        pendingActionId,
        description,
        affectedEntity,
        changes,
        expiresAt: expiresAt ?? Date.now() + 5 * 60 * 1000,
        toolName: toolName ?? "unknown",
      };
    }
  } catch {
    // Not valid JSON, ignore
  }
  return null;
}
